import { useQuery } from "@tanstack/react-query";
import { OmmuseClient } from "../proto/api_grpc_web_pb";
import { GetUsernamesRequest, User } from "../proto/api_pb";
import * as Sentry from "@sentry/react";

type ReturnType = {
  allUsers: User[];
  featuredUsers: User[];
  regularUsers: User[];
  isAllUsersLoading: boolean;
  getAllUsers: () => void;
};

const fetchAllUsers = (): Promise<{
  allUsers: User[];
  featuredUsers: User[];
  regularUsers: User[];
}> => {
  const ommuseClient = new OmmuseClient(process.env.REACT_APP_API_URL!);
  const request = new GetUsernamesRequest();
  const metadata = { "Access-Control-Allow-Origin": "*" };

  return new Promise((resolve, reject) => {
    ommuseClient.getUsernames(request, metadata, (err, response) => {
      if (err) {
        Sentry.captureException(err);
        reject(err);
      } else {
        const featuredUserNameList = response.getFeaturedUsersList();
        const regularUserNameList = response.getRegularUsersList();
        resolve({
          allUsers: featuredUserNameList.concat(regularUserNameList),
          featuredUsers: featuredUserNameList,
          regularUsers: regularUserNameList,
        });
      }
    });
  });
};

export const useGetAllUsers = (): ReturnType => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["allUsers"],
    queryFn: fetchAllUsers,
    staleTime: 10 * 60 * 1000, // 10 min — user list changes rarely
  });

  return {
    allUsers: data?.allUsers ?? [],
    featuredUsers: data?.featuredUsers ?? [],
    regularUsers: data?.regularUsers ?? [],
    isAllUsersLoading: isLoading,
    getAllUsers: () => { refetch(); },
  };
};
